/**
 * '고객 분석'(프로젝트 상세 > 고객 분석) 분포 차트의 **정렬과 라벨**.
 *
 * 왜 프론트에 두는가: 서버(`StatisticsService`)는 이 세 분포를 모두
 * **값이 큰 순서(`sortedByDescending`)** 로 내려준다. 원형 그래프에는 그 순서가 맞지만,
 * 막대그래프로 "어느 나이대·시간대·요일에 몰리는가" 를 읽으려면 **축이 자연 순서**여야 한다
 * (10대 → 60대, 0시 → 24시, 월 → 일). 2026-09-10 사용자 지시.
 *
 * 🔑 서버 응답의 키를 **그대로 쓰지 않고** 표시 라벨로 갈아치운다. 서버 키는 `"20~24"`,
 *    `"9시 ~ 12시"` 처럼 단위가 없거나 띄어쓰기가 길어 축에서 겹친다.
 *
 * 🔴 **모르는 키는 버리지 않고 뒤에 붙인다.** 서버가 구간 문구를 바꾸면(예: `"20~24"` →
 *    `"20-24"`) 매핑이 빗나가는데, 그때 조용히 사라지면 화면이 "고객 0명" 으로 보인다.
 *    뒤에 그대로 붙여 두면 **어긋난 것이 눈에 보인다**. 근거가 되는 서버 코드 =
 *    `StatisticsService.calculateAgeDistribution` / `calculateHoursDistribution` /
 *    `calculateDayOfWeekDistribution`.
 */

/** 연령대 — 서버 키 → 축 라벨. 순서가 곧 x축 순서다(왼쪽부터 어린 순). */
export const AGE_BUCKETS = [
    ['10대이하', '10대 이하'],
    ['20~24', '20~24세'],
    ['25~29', '25~29세'],
    ['30~34', '30~34세'],
    ['35~39', '35~39세'],
    ['40~44', '40~44세'],
    ['45~49', '45~49세'],
    ['50~54', '50~54세'],
    ['55~59', '55~59세'],
    ['60대이상', '60대 이상'],
]

/**
 * 가입 시간대. 서버 else 분기가 `"0시 ~ 6시"` 라 **0~6시가 맨 앞**이다.
 * 구간 폭이 균일하지 않은 것(6~9 는 3시간, 12~13 은 1시간)은 서버 구간을 그대로 따른 것이다.
 */
export const HOUR_BUCKETS = [
    ['0시 ~ 6시', '0~6시'],
    ['6시 ~ 9시', '6~9시'],
    ['9시 ~ 12시', '9~12시'],
    ['12시 ~ 13시', '12~13시'],
    ['13시 ~ 14시', '13~14시'],
    ['14시 ~ 15시', '14~15시'],
    ['15시 ~ 16시', '15~16시'],
    ['16시 ~ 17시', '16~17시'],
    ['17시 ~ 18시', '17~18시'],
    ['18시 ~ 19시', '18~19시'],
    ['19시 ~ 20시', '19~20시'],
    ['20시 ~ 21시', '20~21시'],
    ['21시 ~ 24시', '21~24시'],
]

/**
 * 가입 요일. 서버는 `DayOfWeek.getDisplayName(FULL, KOREAN)` 을 키로 쓰므로
 * 이미 `"월요일"` 형태다 — 라벨은 그대로 두고 **순서만** 월→일로 고정한다.
 */
export const DAY_BUCKETS = [
    ['월요일', '월요일'],
    ['화요일', '화요일'],
    ['수요일', '수요일'],
    ['목요일', '목요일'],
    ['금요일', '금요일'],
    ['토요일', '토요일'],
    ['일요일', '일요일'],
]

/**
 * 서버가 준 분포 객체를 막대그래프용 `{xAxis, series, counts}` 로 바꾼다.
 *
 * - 구간은 `buckets` 순서 그대로 **전부** 나온다(값이 없는 구간은 0). 빈 구간을 빼면
 *   축이 데이터에 따라 들쭉날쭉해져 두 기간을 비교할 수 없다.
 * - `buckets` 에 없는 키는 **원래 문구 그대로 맨 뒤**에 붙는다(위 KDoc 🔴 참고).
 *
 * @param distribution `{ "20~24": 12.5, ... }` 퍼센트(Float)
 * @param countMap 같은 키의 **절대 인원** `{ "20~24": 37, ... }`. 툴팁이 쓴다.
 *   🔴 퍼센트에서 역산하지 않는다 — 분포마다 분모가 다르고(나이대는 생년이 있는 고객만)
 *   반올림도 들어가 있어 인원이 1~2명 틀린다. 서버가 따로 세어 준 값을 그대로 쓴다.
 *   구 응답(이 필드가 없던 시절)에서는 `counts` 가 전부 `null` 이고 툴팁은 퍼센트만 보여준다.
 */
export const toDistributionSeries = (distribution, buckets, countMap = null) => {
    const source = distribution ?? {}
    const counted = countMap ?? null
    const known = new Set(buckets.map(([key]) => key))

    const xAxis = buckets.map(([, label]) => label)
    const series = buckets.map(([key]) => source[key] ?? 0)
    const counts = buckets.map(([key]) => (counted ? (counted[key] ?? 0) : null))

    Object.keys(source)
        .filter((key) => !known.has(key))
        .forEach((key) => {
            xAxis.push(key)
            series.push(source[key])
            counts.push(counted ? (counted[key] ?? 0) : null)
        })

    return { xAxis, series, counts }
}

export const toAgeSeries = (distribution, countMap) =>
    toDistributionSeries(distribution, AGE_BUCKETS, countMap)
export const toHourSeries = (distribution, countMap) =>
    toDistributionSeries(distribution, HOUR_BUCKETS, countMap)
export const toDaySeries = (distribution, countMap) =>
    toDistributionSeries(distribution, DAY_BUCKETS, countMap)

/**
 * 이 기간에 **참여 고객이 한 명도 없는가.**
 *
 * 🔴 왜 필요한가(2026-09-11 사용자 지시): 기본 기간이 '최근 일주일' 이라, **이미 끝난 프로젝트**를
 * 열면 모든 그래프가 빈 축으로 나온다. 그러면 "데이터가 없는 프로젝트" 처럼 보이는데 실제로는
 * **기간을 잘못 보고 있는 것**이다. 빈 축 대신 "기간에 데이터가 없다" 고 말해 줘야 기간을
 * 다시 확인한다.
 *
 * 🔑 판정은 `dateGroups`(일자별 참여 수)로 한다 — 그것이 모집단 그 자체다.
 *    퍼센트 분포로 판정하면 안 된다: 나이·성별은 **수집된 고객만** 세므로, 참여자가 있는데도
 *    전부 미수집이면 빈 객체가 되어 "데이터 없음" 으로 오판한다.
 *
 * ⚠️ 응답을 아직 못 받은 상태(`undefined`/`[]`)도 '없음' 으로 나온다 — 호출부에서 **로딩 중일 때는
 *    이 안내를 띄우지 않는다**(스피너가 그 자리를 쓴다).
 */
export const hasNoParticipants = (customers) => {
    const groups = customers?.dateGroups
    if (!Array.isArray(groups)) return true
    return groups.reduce((sum, row) => sum + (row?.joinCount ?? 0), 0) === 0
}
